import type { Tenant } from '../types/tenant.types';

/**
 * Derive a UI status from the tenant record.
 * New API uses `status` string; legacy used `is_active` boolean.
 */
export function tenantRowStatus(tenant: Tenant): 'active' | 'suspended' | 'trial' {
  if (tenant.status) {
    const s = tenant.status.toLowerCase();
    if (s === 'suspended') return 'suspended';
    if (s === 'trial') return 'trial';
    return 'active';
  }
  if (tenant.is_active === false) return 'suspended';
  if (tenant.trial_ends_at) {
    const end = new Date(tenant.trial_ends_at).getTime();
    if (!Number.isNaN(end) && end > Date.now()) return 'trial';
  }
  return 'active';
}

/**
 * Whether the tenant is currently active (not suspended).
 * Works with both new `status` field and legacy `is_active`.
 */
export function isTenantActive(tenant: Tenant): boolean {
  if (tenant.status) {
    return tenant.status.toLowerCase() !== 'suspended';
  }
  return tenant.is_active !== false;
}

/** Workspace slug used at tenant login (SaaS single-app model). */
export function tenantWorkspaceSlug(tenant: Tenant): string {
  return tenant.slug?.trim() || '—';
}

/** @deprecated Use tenantWorkspaceSlug — domains/subdomains are not required for login. */
export function storefrontDomainSlug(tenant: Tenant): string {
  return tenantWorkspaceSlug(tenant);
}

/** First domain in API order (legacy, optional). */
export function primaryDomain(tenant: Tenant): string {
  return tenant.domains?.[0]?.domain ?? '—';
}

/** @deprecated Legacy backend domain marker. */
export function isBackendDomainSlug(domain: string): boolean {
  return domain.trim().toLowerCase().endsWith('.backend');
}

export function formatTenantDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
